// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: date plumbing shared by the create/edit event form.
//
// The form always holds `YYYY-MM-DDTHH:mm` values, even for all-day events, so
// toggling "All day" never has to reformat state. Graph, on the other hand,
// wants all-day events pinned to midnight with an *exclusive* end date, so the
// last day of an all-day event is shifted on the way in and out.

/** `YYYY-MM-DDTHH:mm` for a datetime-local input, in the viewer's zone. */
export function toLocalInput(value: Date): string {
	return new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/** Shift a `YYYY-MM-DD...` value by whole days, returning `YYYY-MM-DD`. */
export function shiftDate(value: string, days: number): string {
	const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) return value.slice(0, 10);
	date.setUTCDate(date.getUTCDate() + days);
	return date.toISOString().slice(0, 10);
}

/** Graph's exclusive end date → the last day the form should show. */
export function allDayEndToInclusive(value: string): string {
	return shiftDate(value, -1);
}

/** The form's last day → the exclusive midnight boundary Graph expects. */
export function allDayEndToExclusive(value: string): string {
	return `${shiftDate(value, 1)}T00:00:00`;
}

/** What to send for an event's start/end given the all-day toggle. */
export function eventTimesForRequest(start: string, end: string, allDay: boolean): {start: string; end: string} {
	if (!allDay) return {start, end};
	const startDate = start.slice(0, 10);
	const endDate = end.slice(0, 10) < startDate ? startDate : end.slice(0, 10);
	return {start: `${startDate}T00:00:00`, end: allDayEndToExclusive(endDate)};
}

/** Whether the form's end is a valid boundary after its start. */
export function isValidEventTimeRange(start: string, end: string, allDay: boolean): boolean {
	if (!start || !end) return false;
	if (allDay) return end.slice(0, 10) >= start.slice(0, 10);
	const startTime = new Date(start).getTime();
	const endTime = new Date(end).getTime();
	return Number.isFinite(startTime) && Number.isFinite(endTime) && endTime > startTime;
}

/** Move an end forward when a changed start would otherwise overtake it. */
export function endAfterStartChange(previousStart: string, nextStart: string, end: string, allDay: boolean): string {
	if (!previousStart || !nextStart || !end || isValidEventTimeRange(nextStart, end, allDay)) return end;

	if (allDay) {
		const previousStartDate = new Date(`${previousStart.slice(0, 10)}T00:00:00Z`).getTime();
		const endDate = new Date(`${end.slice(0, 10)}T00:00:00Z`).getTime();
		const durationDays = Math.max(0, Math.round((endDate - previousStartDate) / 86_400_000));
		return `${shiftDate(nextStart, durationDays)}T00:00`;
	}

	const previousStartTime = new Date(previousStart).getTime();
	const nextStartTime = new Date(nextStart).getTime();
	const endTime = new Date(end).getTime();
	if (!Number.isFinite(nextStartTime)) return end;
	const duration =
		Number.isFinite(previousStartTime) && Number.isFinite(endTime) && endTime > previousStartTime
			? endTime - previousStartTime
			: 30 * 60 * 1000;
	return toLocalInput(new Date(nextStartTime + duration));
}

/** `guildId:channelId` for the voice-channel picker, from a Konvo join link. */
export function channelKeyFromJoinUrl(url: string | null): string {
	if (!url) return '';
	try {
		const segments = new URL(url).pathname.split('/').filter(Boolean);
		const index = segments.indexOf('channels');
		if (index === -1 || segments.length < index + 3) return '';
		return `${segments[index + 1]}:${segments[index + 2]}`;
	} catch {
		return '';
	}
}

/** Public meetings keep their link when unrelated event fields are edited. */
export function isPublicMeetingUrl(url: string | null): boolean {
	if (!url) return false;
	try {
		return /^\/meet\/v2\.[A-Za-z0-9_-]{80,4096}$/.test(new URL(url).pathname);
	} catch {
		return false;
	}
}
